import { PictureAsPdf } from '@mui/icons-material';
import { DialogActions, DialogContent, DialogTitle, Dialog as MuiDialog } from '@mui/material';
import { Badge, Dialog, Loader, Tab, TabView, useDialog } from 'components';
import { useAuth } from 'contexts';
import dayjs from 'dayjs';
import { default as utc } from 'dayjs/plugin/utc';
import NotFound from 'pages/not-found';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { deleteInvoice, downloadPdf, fetchInvoice, setInvoiceStatusReasons, setInvoiceStatuses } from 'services';
import { Invoice, InvoiceStatus, Role } from 'types';
import '../hold-card.scss';
import { InvoiceDetailsCredits } from './invoice-details-credits';
import { InvoiceDetailsTab } from './invoice-details-tab';
import { InvoiceSummaryTab } from './invoice-summary-tab';

dayjs.extend(utc);

export const InvoiceDetailsOverview: FC = () => {
  // STATE
  const params = useParams();
  const navigate = useNavigate();

  const { userProfile } = useAuth();
  const { ref: holdDialogRef, openDialog: openHoldDialog, closeDialog: closeHoldDialog } = useDialog({});

  const [invoiceId] = useState<number>(Number(params.invoiceId));
  const [loading, setLoading] = useState(false);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [reloadTrigger, setReloadTrigger] = useState(Math.random());
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [updateStatusLoading, setUpdateStatusLoading] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [holdReason, setHoldReason] = useState('');

  const tabs: Tab[] = useMemo(
    () => [
      {
        label: 'Invoice Summary',
        content: <InvoiceSummaryTab invoice={invoice} />
      },
      {
        label: 'Invoice Details',
        content: <InvoiceDetailsTab invoice={invoice} onItemEditSuccess={() => setReloadTrigger(Math.random())} />
      },
      {
        label: 'Unit Credits',
        content: <>{!!invoice && <InvoiceDetailsCredits invoice={invoice} />}</>
      }
    ],
    [invoice]
  );

  const setStatus = useCallback(
    (status: InvoiceStatus, holdReason?: string) => {
      if (!invoice) {
        return;
      }
      setUpdateStatusLoading(true);
      setInvoiceStatuses({ invoiceIds: [invoice.id], status })
        .then(invoices => {
          if (invoices && invoices[0] && holdReason) {
            return setInvoiceStatusReasons({ invoiceIds: [invoices[0].id], statusReason: holdReason });
          } else return invoices;
        })
        .then(invoices => {
          if (invoices !== null) {
            // Changing the key re-renders the tab which reloads the list
            navigate('/invoices', { replace: true });
          }
        })
        .finally(() => setUpdateStatusLoading(false));
    },
    [invoice, navigate]
  );

  const getBadge = useCallback(() => {
    if (invoice?.finalized) {
      return <></>;
    }
    switch (invoice?.status) {
      case InvoiceStatus.NEEDS_REVIEW:
        return <></>;
      case InvoiceStatus.APPROVED:
        return <Badge text="Approved" backgroundColor="var(--green)" />;
      case InvoiceStatus.HOLD:
        return (
          <>
            <span className="overflow">
              <Badge text="Hold" backgroundColor="var(--red)" />
            </span>
            <span className="tooltip translate-right-20pc">{invoice.statusReason}</span>
          </>
        );
    }
  }, [invoice?.finalized, invoice?.status]);

  const doDelete = useCallback(() => {
    if (!invoice) {
      return;
    }
    setDeleteLoading(true);
    deleteInvoice(invoice.id).finally(() => {
      setDeleteLoading(false);
      navigate('/invoices', { replace: true });
    });
  }, [invoice, navigate]);

  // EFFECTS
  // Load Invoice
  useEffect(() => {
    if (isNaN(invoiceId)) {
      return;
    }
    setLoading(true);
    fetchInvoice(invoiceId).then(i => {
      setInvoice(i);
      setLoading(false);
    });
  }, [invoiceId, reloadTrigger]);

  if ((invoice === null || isNaN(invoiceId)) && !loading) {
    return (
      <div className="page">
        <NotFound message="The invoice you tried to access doesn't exist." />
      </div>
    );
  }

  return (
    <Loader loading={loading}>
      <MuiDialog open={showDeleteConfirmation}>
        <DialogTitle>Delete Invoice?</DialogTitle>
        <DialogContent>
          <p>Are you sure you want to delete this invoice?</p>
        </DialogContent>
        <DialogActions>
          <div className="d-flex g-2">
            <button
              className="btn-outline"
              onClick={() => {
                setShowDeleteConfirmation(false);
              }}
            >
              Cancel
            </button>
            <Loader loading={deleteLoading} scale="50%">
              <button
                className="btn-danger"
                onClick={() => {
                  doDelete();
                }}
              >
                Delete
              </button>
            </Loader>
          </div>
        </DialogActions>
      </MuiDialog>
      <Dialog ref={holdDialogRef} closeDialog={closeHoldDialog} data-cy="invoice-hold-dialog">
        <div className="dialog-header">
          <h3>Hold Invoice</h3>
        </div>
        <div className="dialog-body">
          <p className="margin-bottom-16px">Why are you holding this invoice?</p>
          <input type="text" value={holdReason} onChange={({ target }) => setHoldReason(target.value)} />
        </div>
        <div className="dialog-footer g-2">
          <button className="btn-outline" onClick={closeHoldDialog}>
            Cancel
          </button>
          <button
            className="btn-danger"
            onClick={() => {
              setStatus(InvoiceStatus.HOLD, holdReason);
              closeHoldDialog();
            }}
          >
            <span>Hold</span>
          </button>
        </div>
      </Dialog>
      <div className="page">
        <div className="page-header mb-1">
          <div>
            <div className="d-flex g-3 align-items-center wrap">
              <h1 className="text-uppercase text-primary">{invoice?.customer.name}</h1>
              {getBadge()}
            </div>
          </div>
          <div className="d-flex align-items-center g-3">
            <button className="btn-back" onClick={() => navigate('/invoices', { replace: true })}>
              <span className="material-icons">arrow_back_ios</span>
              BACK
            </button>
            {[Role.ADMIN, Role.BILLING_ADMIN, Role.SENIOR_BOOKING].includes(userProfile.role) && (
              <button
                className="btn-danger"
                onClick={() => setShowDeleteConfirmation(true)}
                data-cy="invoice-delete-button"
              >
                Delete
              </button>
            )}
            {!invoice?.finalized && (
              <>
                <Loader loading={updateStatusLoading} scale="50%">
                  <button className="btn-outline" onClick={() => openHoldDialog()} data-cy="invoice-hold-button">
                    <div className="d-flex align-items-center g-2">
                      <span>Hold</span>
                    </div>
                  </button>
                </Loader>
                <Loader loading={updateStatusLoading} scale="50%">
                  <button
                    className="btn-primary"
                    onClick={() => setStatus(InvoiceStatus.APPROVED)}
                    data-cy="invoice-approve-button"
                  >
                    <div className="d-flex align-items-center g-2">
                      <span>Approve</span>
                    </div>
                  </button>
                </Loader>
              </>
            )}
            {!!invoice?.finalized && (
              <>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => downloadPdf(invoice.id)}
                  data-cy="invoice-download-button"
                >
                  <div className="d-flex align-items-center g-2">
                    <span>Download PDF</span>
                    <PictureAsPdf fontSize="small" />
                  </div>
                </button>
              </>
            )}
          </div>
        </div>
        <div className="d-flex g-2 mb-3">
          {invoice?.finalized && <h4 className="m-0">Reference #: {invoice.invoiceNumber}</h4>}
          {invoice?.finalized && !!invoice.invoiceDate && (
            <h4 className="m-0">Invoice Date: {dayjs.utc(invoice.invoiceDate).format('MM/DD/YYYY')}</h4>
          )}
          <h4 className="m-0 font-weight-medium"> {dayjs.utc(invoice?.fromDate).format('MM/DD/YYYY')}</h4>
          <h4 className="m-0 font-weight-medium">-</h4>
          <h4 className="m-0 font-weight-medium">{dayjs.utc(invoice?.toDate).format('MM/DD/YYYY')}</h4>
        </div>
        <div className="page-content flex-grow-1">
          <TabView tabs={tabs} />
        </div>
      </div>
    </Loader>
  );
};
